/**
 * TREATX-LEGAL-MULTILINGUAL-ADMIN-01
 * ----------------------------------
 * Per-locale legal-body editor.
 *
 * Dynamic locale selector — the tab strip is derived from
 *   GET /api/admin/legal/supported-locales
 * unioned with any locale already present on the draft, so future
 * languages can be enabled by env override without a code change.
 *
 * Saves go through the LOCALE-SAFE endpoint
 *   PATCH /api/admin/legal/documents/{content_key}/versions/{version}/locale/{locale}
 * which uses Mongo dot-notation to update ONLY the selected locale's
 * body / body_sha256 / title / summary — sibling locales are never
 * touched.
 *
 * Never mutates published versions. If the document only has a
 * published version, POST /ensure-draft is invoked once to produce
 * a companion draft that inherits the published body verbatim.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Globe2, Loader2, Save, Eye, Languages, ShieldAlert,
  CheckCircle2, AlertTriangle, FileUp, Code2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { SafeHtml } from '../../components/security/SafeHtml';
import { api } from '../../utils/api';

const LOCALE_LABEL = {
  en: 'English', tr: 'Türkçe', de: 'Deutsch', es: 'Español', fr: 'Français',
  it: 'Italiano', ar: 'العربية', ru: 'Русский',
};

const humanDate = (iso) => (iso ? formatDateTimeLocale(iso) : '—');
const shortSha = (s) => (s ? `${s.slice(0, 12)}…` : '—');

const LegalMultilingualEditor = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const contentKey = params.get('key') || '';
  const initialLocale = (params.get('locale') || '').toLowerCase();

  const [supportedLocales, setSupportedLocales] = useState([]);
  const [controllingLocale, setControllingLocale] = useState('en');
  const [mandatoryLocales, setMandatoryLocales] = useState([]);
  const [overview, setOverview] = useState(null);
  const [draft, setDraft] = useState(null);
  const [activeLocale, setActiveLocale] = useState(initialLocale || 'en');
  const [initialLocaleApplied, setInitialLocaleApplied] = useState(!initialLocale);
  const [bodyDraft, setBodyDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [summaryDraft, setSummaryDraft] = useState('');
  // P0-PROVIDER-DOC-SHARE-DECLARATION — per-locale acceptance (checkbox)
  // wording. Same locale-safe dot-notation patch as title/summary/body.
  const [acceptanceLabelDraft, setAcceptanceLabelDraft] = useState('');
  const [translatorNote, setTranslatorNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [error, setError] = useState('');
  // DOCX render-preview → confirm → save flow (primary UX).
  const [docxBusy, setDocxBusy] = useState(false);
  const [renderResult, setRenderResult] = useState(null); // pending preview awaiting confirm
  const [sourceMeta, setSourceMeta] = useState(null);      // {source_sha256, source_filename} of confirmed DOCX
  const [showAdvanced, setShowAdvanced] = useState(false);

  const loadAll = useCallback(async () => {
    if (!contentKey) return;
    setLoading(true);
    setError('');
    try {
      const [locRes, ovRes, polRes] = await Promise.all([
        api.get('/admin/legal/supported-locales'),
        api.get(`/admin/legal/documents/${encodeURIComponent(contentKey)}/locale-overview`),
        api.get(`/admin/legal/documents/${encodeURIComponent(contentKey)}/locale-policy`),
      ]);
      const supported = locRes.data.supported_locales || ['en'];
      setSupportedLocales(supported);
      setOverview(ovRes.data);
      setControllingLocale(polRes.data.controlling_locale || 'en');
      setMandatoryLocales(polRes.data.mandatory_locales || []);
      // OWNER UAT — honour ?locale= deep-link exactly once so admins
      // landing from "Edit Türkçe" arrive on the TR tab. After the first
      // application, respect user's tab clicks.
      if (!initialLocaleApplied && initialLocale) {
        if (supported.includes(initialLocale) || initialLocale === (polRes.data.controlling_locale || 'en')) {
          setActiveLocale(initialLocale);
        }
        setInitialLocaleApplied(true);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message || e.message;
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      toast.error('Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [contentKey]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const ensureDraft = useCallback(async () => {
    if (!contentKey) return null;
    setEnsuring(true);
    try {
      const res = await api.post(
        `/admin/legal/documents/${encodeURIComponent(contentKey)}/ensure-draft`,
      );
      toast.success(
        res.data.outcome === 'draft_created'
          ? `New draft v${res.data.version} cloned from published`
          : `Draft v${res.data.version} loaded`,
      );
      setDraft(res.data.draft || null);
      await loadAll();
      return res.data.draft;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data;
      toast.error(
        (detail && (detail.message || detail.code)) || 'Failed to open draft',
      );
      return null;
    } finally {
      setEnsuring(false);
    }
  }, [contentKey, loadAll]);

  // Autoload the draft version snapshot when overview arrives.
  useEffect(() => {
    if (!overview) return;
    if (overview.draft) {
      // fetch the full draft body via admin/agreements versions API
      (async () => {
        try {
          const r = await api.get(
            `/admin/agreements/${encodeURIComponent(contentKey)}/versions`,
          );
          const drafts = (r.data.versions || [])
            .filter((v) => (v.status || '').toLowerCase() === 'draft')
            .sort((a, b) => b.version - a.version);
          if (drafts.length) setDraft(drafts[0]);
        } catch (e) {
          // best-effort — the overview snapshot is still usable
        }
      })();
    }
  }, [overview, contentKey]);

  // When active locale or draft changes, reset editor fields.
  useEffect(() => {
    if (!draft) {
      setBodyDraft(''); setTitleDraft(''); setSummaryDraft(''); setTranslatorNote('');
      setDirty(false); setRenderResult(null); setSourceMeta(null); setShowAdvanced(false);
      return;
    }
    setBodyDraft((draft.body || {})[activeLocale] || '');
    setTitleDraft((draft.title || {})[activeLocale] || '');
    setSummaryDraft((draft.summary || {})[activeLocale] || '');
    setAcceptanceLabelDraft((draft.acceptance_label || {})[activeLocale] || '');
    setTranslatorNote((draft.locale_notes || {})[activeLocale] || '');
    setDirty(false); setRenderResult(null); setSourceMeta(null); setShowAdvanced(false);
  }, [draft, activeLocale]);

  const renderDocx = async (fileInput) => {
    const f = fileInput?.files?.[0];
    if (!f) return;
    if (!draft) { toast.error(t('agreements.mlex.draftRequired')); return; }
    if (!f.name.toLowerCase().endsWith('.docx')) {
      toast.error(t('agreements.mlex.renderFailed'));
      fileInput.value = '';
      return;
    }
    setDocxBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(
        `/admin/legal/documents/${encodeURIComponent(contentKey)}/versions/${draft.version}/locale/${activeLocale}/render-docx`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setRenderResult(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d.code)) || t('agreements.mlex.renderFailed'));
    } finally {
      setDocxBusy(false);
      if (fileInput) fileInput.value = '';
    }
  };

  const confirmRender = () => {
    if (!renderResult) return;
    setBodyDraft(renderResult.html || '');
    setSourceMeta({
      source_sha256: renderResult.source_sha256,
      source_filename: renderResult.source_filename,
    });
    setDirty(true);
    setRenderResult(null);
  };

  const discardRender = () => setRenderResult(null);

  // Tabs = union of supported + any locale already stored on the draft
  const tabLocales = useMemo(() => {
    const s = new Set(supportedLocales);
    if (draft?.body) Object.keys(draft.body).forEach((k) => s.add(k));
    // Ensure controlling locale is first
    const out = Array.from(s);
    out.sort((a, b) => {
      if (a === controllingLocale) return -1;
      if (b === controllingLocale) return 1;
      const ia = supportedLocales.indexOf(a);
      const ib = supportedLocales.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
    return out;
  }, [supportedLocales, draft, controllingLocale]);

  const savePatch = async () => {
    if (!draft) return toast.error('Open a draft first');
    if (activeLocale === controllingLocale) {
      const proceed = window.confirm(
        `You are editing the CONTROLLING locale (${controllingLocale.toUpperCase()}). ` +
        'This is the source of legal truth. Continue anyway?',
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const payload = {
        body_html: bodyDraft,
        ...(titleDraft ? { title: titleDraft } : {}),
        ...(summaryDraft ? { summary: summaryDraft } : {}),
        ...(acceptanceLabelDraft ? { acceptance_label: acceptanceLabelDraft } : {}),
        ...(translatorNote ? { translator_note: translatorNote } : {}),
        ...(sourceMeta?.source_sha256 ? { source_sha256: sourceMeta.source_sha256 } : {}),
        ...(sourceMeta?.source_filename ? { source_filename: sourceMeta.source_filename } : {}),
      };
      const r = await api.patch(
        `/admin/legal/documents/${encodeURIComponent(contentKey)}/versions/${draft.version}/locale/${activeLocale}`,
        payload,
      );
      toast.success(`Saved ${activeLocale.toUpperCase()} · draft v${draft.version}`);
      setDirty(false);
      // Merge updated body/sha back into draft so sibling tabs remain intact
      setDraft((prev) => {
        if (!prev) return prev;
        const nb = { ...(prev.body || {}), [activeLocale]: bodyDraft };
        const ns = { ...(prev.body_sha256 || {}), [activeLocale]: (r.data.body_sha256 || {})[activeLocale] };
        const nt = titleDraft
          ? { ...(prev.title || {}), [activeLocale]: titleDraft }
          : prev.title;
        const nsum = summaryDraft
          ? { ...(prev.summary || {}), [activeLocale]: summaryDraft }
          : prev.summary;
        const nacc = acceptanceLabelDraft
          ? { ...(prev.acceptance_label || {}), [activeLocale]: acceptanceLabelDraft }
          : prev.acceptance_label;
        return {
          ...prev, body: nb, body_sha256: ns, title: nt, summary: nsum,
          acceptance_label: nacc,
        };
      });
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (detail && (detail.message || detail.code)) ||
        e?.response?.data?.message ||
        'Save failed',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!contentKey) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="mlex-empty">
        <a href="#/treatx-admin/agreements"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3"
          data-testid="mlex-back">
          <ArrowLeft className="h-3 w-3" /> Back to Agreement Library
        </a>
        <h1 className="text-2xl font-semibold text-slate-900">Multilingual Legal Editor</h1>
        <p className="text-sm text-slate-500 mt-2">
          Open a document from the Agreement Library and click <b>Manage Locales</b>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="legal-multilingual-editor">
      <a href="#/treatx-admin/agreements"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3"
        data-testid="mlex-back">
        <ArrowLeft className="h-3 w-3" /> Back to Agreement Library
      </a>

      <div className="flex items-center gap-3 mb-1">
        <Languages className="h-5 w-5 text-slate-600" />
        <h1 className="text-2xl font-semibold text-slate-900">
          Multilingual editor — <code className="text-lg">{contentKey}</code>
        </h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Edit each locale independently on the SAME canonical draft version. Published
        versions are immutable; open a draft first, then translate.
      </p>

      {/* Primary workflow is now DOCX upload → render preview → confirm.
          The raw-HTML editor is retained only as an explicit advanced option. */}
      <div className="mb-4 border border-sky-200 bg-sky-50 rounded p-3 text-xs text-sky-900" data-testid="mlex-input-format-notice">
        <b>{t('agreements.mlex.inputFormatTitle')}:</b> {t('agreements.mlex.inputFormatBody')}
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-800" data-testid="mlex-error">
          <AlertTriangle className="h-4 w-4 inline mr-1" /> {error}
        </div>
      ) : (
        <>
          {/* Header card: policy + version state */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border rounded-xl bg-white p-4">
              <div className="text-xs uppercase text-slate-500 mb-1">Locale policy</div>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-xs text-slate-500">Controlling:</span>
                <Badge className="bg-emerald-100 text-emerald-800" data-testid="mlex-controlling">
                  {controllingLocale.toUpperCase()}
                </Badge>
                <span className="text-xs text-slate-500 ml-3">Mandatory:</span>
                {mandatoryLocales.length === 0 && <span className="text-xs text-slate-400">none</span>}
                {mandatoryLocales.map((m) => (
                  <Badge key={m} className="bg-amber-100 text-amber-800" data-testid={`mlex-mandatory-${m}`}>
                    {m.toUpperCase()}
                  </Badge>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Supported locales (env): {supportedLocales.map((s) => s.toUpperCase()).join(' · ')}
              </div>
            </div>
            <div className="border rounded-xl bg-white p-4">
              <div className="text-xs uppercase text-slate-500 mb-1">Version state</div>
              <div className="text-sm">
                <div>Current published: <b data-testid="mlex-cur-ver">v{overview?.current_version || '—'}</b></div>
                <div>Published locales: {(overview?.published?.locales_present || []).map((l) => (
                  <Badge key={l} className="bg-emerald-100 text-emerald-800 mr-1">{l.toUpperCase()}</Badge>
                ))}</div>
                <div className="mt-1">Draft version: {draft
                  ? <b>v{draft.version}</b>
                  : <span className="text-slate-400">no draft</span>}
                </div>
                <div>Draft locales: {(draft?.body ? Object.keys(draft.body).filter((k) => (draft.body[k] || '').trim()) : []).map((l) => (
                  <Badge key={l} className="bg-sky-100 text-sky-800 mr-1" data-testid={`mlex-draft-loc-${l}`}>{l.toUpperCase()}</Badge>
                ))}</div>
              </div>
              {!draft && (
                <Button onClick={ensureDraft} disabled={ensuring} className="mt-3" data-testid="mlex-ensure-draft-btn">
                  {ensuring ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Open / create draft
                </Button>
              )}
            </div>
          </div>

          {/* Locale tabs */}
          <div className="border rounded-t-xl bg-white flex flex-wrap gap-1 px-2 pt-2" data-testid="mlex-locale-tabs">
            {tabLocales.map((loc) => {
              const isActive = loc === activeLocale;
              const hasBody = !!(draft?.body || {})[loc]?.trim();
              const isControl = loc === controllingLocale;
              return (
                <button
                  key={loc}
                  data-testid={`mlex-tab-${loc}`}
                  onClick={() => {
                    if (dirty) {
                      if (!window.confirm('You have unsaved edits. Switch locale anyway?')) return;
                    }
                    setActiveLocale(loc);
                  }}
                  className={`px-3 py-2 text-sm rounded-t-md border-b-2 flex items-center gap-1 ${
                    isActive
                      ? 'border-emerald-500 text-emerald-700 bg-white font-semibold'
                      : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <Globe2 className="h-3 w-3" />
                  {LOCALE_LABEL[loc] || loc.toUpperCase()} <span className="text-xs opacity-70">({loc})</span>
                  {isControl && <Badge className="ml-1 bg-emerald-100 text-emerald-800 text-[10px]">SRC</Badge>}
                  {hasBody
                    ? <CheckCircle2 className="h-3 w-3 text-emerald-500" data-testid={`mlex-has-${loc}`} />
                    : <span className="text-[10px] text-slate-400">empty</span>}
                </button>
              );
            })}
          </div>

          {/* Editor + preview */}
          <div className="border border-t-0 rounded-b-xl bg-white p-4">
            {!draft ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                No draft version yet. Click <b>Open / create draft</b> above.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="text-sm">
                    Editing <b>{LOCALE_LABEL[activeLocale] || activeLocale.toUpperCase()}</b> — draft v{draft.version}
                    {activeLocale === controllingLocale && (
                      <Badge className="ml-2 bg-emerald-100 text-emerald-800">Controlling locale</Badge>
                    )}
                    {mandatoryLocales.includes(activeLocale) && (
                      <Badge className="ml-1 bg-amber-100 text-amber-800">Mandatory</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={savePatch}
                      disabled={saving || !dirty || !bodyDraft.trim()}
                      data-testid="mlex-save-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                      {saving ? t('agreements.mlex.saving') : t('agreements.mlex.saveBtn')}
                    </Button>
                  </div>
                </div>

                {/* ── PRIMARY: DOCX upload → render preview → confirm ── */}
                {renderResult ? (
                  <div className="border rounded-lg bg-white mb-4" data-testid="mlex-render-preview-card">
                    <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        <Eye className="h-4 w-4" /> {t('agreements.mlex.previewTitle')}
                        <span className="text-xs text-slate-500">· {renderResult.source_filename}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {renderResult.render_integrity_status === 'PASS' ? (
                          <Badge className="bg-emerald-100 text-emerald-800" data-testid="mlex-integrity-pass">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> {t('agreements.mlex.integrityPass')}
                          </Badge>
                        ) : (
                          <Badge className="bg-rose-100 text-rose-800" data-testid="mlex-integrity-fail">
                            <AlertTriangle className="h-3 w-3 mr-1" /> {t('agreements.mlex.integrityFail')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-2 text-xs text-slate-500 border-b" data-testid="mlex-render-stats">
                      {t('agreements.mlex.stats', {
                        headings: renderResult.stats?.headings ?? 0,
                        tables: renderResult.stats?.tables ?? 0,
                        lists: renderResult.stats?.lists ?? 0,
                        chars: renderResult.stats?.chars ?? 0,
                      })}
                    </div>
                    {renderResult.stripped_active_content && (
                      <div className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b" data-testid="mlex-stripped-warn">
                        <ShieldAlert className="h-3 w-3 inline mr-1" /> {t('agreements.mlex.strippedWarn')}
                      </div>
                    )}
                    <SafeHtml
                      as="article"
                      html={renderResult.html || `<em>${t('agreements.mlex.previewEmpty')}</em>`}
                      className="prose prose-sm max-w-none p-4 max-h-[500px] overflow-auto"
                      testId="mlex-render-preview-body"
                    />
                    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-slate-50">
                      <Button variant="outline" size="sm" onClick={discardRender} data-testid="mlex-render-discard">
                        {t('agreements.mlex.reupload')}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={confirmRender}
                        disabled={renderResult.render_integrity_status !== 'PASS'}
                        data-testid="mlex-render-confirm"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> {t('agreements.mlex.confirmContinue')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 mb-4 bg-slate-50/50" data-testid="mlex-docx-uploader">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      <FileUp className="h-4 w-4 text-emerald-600" /> {t('agreements.mlex.uploadTitle')}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{t('agreements.mlex.uploadHint')}</p>
                    <label className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 rounded border bg-white text-xs cursor-pointer hover:bg-slate-50" data-testid="mlex-docx-upload-label">
                      {docxBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileUp className="h-3 w-3" />}
                      {docxBusy ? t('agreements.mlex.rendering') : t('agreements.mlex.uploadBtn')}
                      <input
                        type="file"
                        accept=".docx"
                        className="hidden"
                        disabled={docxBusy}
                        data-testid="mlex-docx-upload-input"
                        onChange={(e) => renderDocx(e.target)}
                      />
                    </label>
                    <p className="text-[11px] text-slate-400 mt-2">
                      <ShieldAlert className="h-3 w-3 inline mr-1" />{t('agreements.mlex.securityNote')}
                    </p>
                  </div>
                )}

                {/* ── Metadata + confirmed body (only once content exists) ── */}
                {(bodyDraft.trim() || titleDraft || summaryDraft) && !renderResult && (
                  <>
                    <div className="text-sm font-semibold text-slate-800 mb-2">{t('agreements.mlex.metadataTitle')}</div>
                    {sourceMeta?.source_filename && (
                      <div className="text-xs text-slate-500 mb-2" data-testid="mlex-source-meta">
                        <FileUp className="h-3 w-3 inline mr-1" />{sourceMeta.source_filename}
                        {sourceMeta.source_sha256 && <span className="ml-2">· sha: {shortSha(sourceMeta.source_sha256)}</span>}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <label className="block">
                        <span className="text-xs uppercase text-slate-500">{t('agreements.mlex.fieldTitle')} ({activeLocale})</span>
                        <Input
                          value={titleDraft}
                          onChange={(e) => { setTitleDraft(e.target.value); setDirty(true); }}
                          data-testid="mlex-title-input"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs uppercase text-slate-500">{t('agreements.mlex.fieldSummary')} ({activeLocale})</span>
                        <Input
                          value={summaryDraft}
                          onChange={(e) => { setSummaryDraft(e.target.value); setDirty(true); }}
                          data-testid="mlex-summary-input"
                        />
                      </label>
                      <label className="block col-span-2">
                        <span className="text-xs uppercase text-slate-500">{t('agreements.mlex.fieldAcceptanceLabel')} ({activeLocale})</span>
                        <Input
                          value={acceptanceLabelDraft}
                          onChange={(e) => { setAcceptanceLabelDraft(e.target.value); setDirty(true); }}
                          data-testid="mlex-acceptance-label-input"
                        />
                      </label>
                    </div>

                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs uppercase text-slate-500">{t('agreements.mlex.previewTitle')} ({activeLocale})</span>
                      <span className="text-xs text-slate-400">
                        sha: {shortSha((draft.body_sha256 || {})[activeLocale])} · chars: {bodyDraft.length}
                      </span>
                    </div>
                    <SafeHtml
                      as="article"
                      html={bodyDraft || `<em>${t('agreements.mlex.previewEmpty')}</em>`}
                      className="prose prose-sm max-w-none border rounded p-3 bg-slate-50 max-h-[500px] overflow-auto"
                      testId="mlex-preview"
                    />

                    <label className="block mt-3">
                      <span className="text-xs uppercase text-slate-500">{t('agreements.mlex.fieldNote')}</span>
                      <Input
                        value={translatorNote}
                        onChange={(e) => setTranslatorNote(e.target.value)}
                        placeholder={t('agreements.mlex.notePlaceholder')}
                        data-testid="mlex-note-input"
                      />
                    </label>

                    {/* Advanced (opt-in) raw HTML editor — hidden by default */}
                    <div className="mt-3 border-t pt-3">
                      <button
                        type="button"
                        onClick={() => setShowAdvanced((v) => !v)}
                        className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                        data-testid="mlex-advanced-toggle"
                      >
                        <Code2 className="h-3 w-3" />
                        {showAdvanced ? t('agreements.mlex.advancedHide') : t('agreements.mlex.advancedToggle')}
                      </button>
                      {showAdvanced && (
                        <Textarea
                          rows={16}
                          value={bodyDraft}
                          onChange={(e) => { setBodyDraft(e.target.value); setDirty(true); setSourceMeta(null); }}
                          className="font-mono text-xs mt-2"
                          data-testid="mlex-body-textarea"
                        />
                      )}
                    </div>
                  </>
                )}

                <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 border-t pt-3">
                  <ShieldAlert className="h-4 w-4 mt-0.5 text-amber-500" />
                  <div>{t('agreements.mlex.additiveNote')}</div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LegalMultilingualEditor;
